import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star } from "lucide-react";
import Counter from "@/components/site/Counter";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] } }),
};

const HEADLINE = ["Premium digital", "products engineered", "to win."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="hero">
      {/* background layers */}
      <div className="absolute inset-0 grid-bg radial-fade opacity-70" />
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute left-1/2 top-24 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-vantage/20 blur-[140px]"
      />

      <div className="container-wv relative">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-slate-300"
          data-testid="hero-badge"
        >
          <span className="flex h-2 w-2 rounded-full bg-emerald-400" />
          Available for new projects — Q1 2026
        </motion.div>

        <h1 className="max-w-4xl font-heading text-5xl font-bold leading-[0.95] tracking-tighter sm:text-6xl lg:text-8xl">
          {HEADLINE.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 2 ? (
                  <>
                    to <span className="text-gradient">win.</span>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-8 max-w-xl text-lg leading-relaxed text-slate-400"
        >
          We are a premium digital agency crafting award-winning websites, custom applications,
          AI automation and SEO that turn ambitious brands into market leaders.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/contact" className="btn-primary group" data-testid="hero-cta-primary">
            Start your project
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <Link to="/portfolio" className="btn-ghost" data-testid="hero-cta-secondary">
            View our work
          </Link>
        </motion.div>

        {/* hero visual with parallax */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-20"
        >
          <div className="relative overflow-hidden rounded-[28px] border border-white/10 shadow-[0_40px_120px_-40px_rgba(0,102,255,0.5)]">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src="https://images.unsplash.com/photo-1679669693872-12d991fc9d93?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Web Vantage digital craftsmanship"
              className="h-[300px] w-full object-cover sm:h-[440px] lg:h-[560px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/30 to-transparent" />
          </div>

          {/* floating stat chips */}
          <div className="absolute -bottom-6 left-4 flex flex-wrap gap-4 sm:left-8">
            <div className="glass-strong rounded-2xl px-5 py-4">
              <p className="font-heading text-2xl font-bold text-white"><Counter to={100} suffix="/100" /></p>
              <p className="text-xs text-slate-400">Desktop PageSpeed</p>
            </div>
            <div className="glass-strong hidden rounded-2xl px-5 py-4 sm:block">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-vantage text-vantage" />)}
              </div>
              <p className="mt-1 text-xs text-slate-400">Rated 5.0 by clients</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
